import { useState } from "react";
import { Button, Card, Icon, Tag, useConfirmSafe } from "@xinghai/ui";
import type { Tone } from "@xinghai/ui";
import {
  clearQueue,
  pendingCount,
  removeFromQueue,
  retryNow,
  useUploadQueue,
} from "../../lib/queue";
import type { QueuedState, QueuedUpload } from "../../lib/queue";
import { useOnline } from "../../app/useOnline";
import { formatDuration } from "./taskMeta";

/** docs/UX.md §1.5 — 一个词说清一条在干什么，不用颜色单独承担意义。 */
const STATE_LABEL: Record<
  QueuedState,
  { label: string; tone: Tone; icon: "ui-clock" | "ui-upload" | "ui-refresh" | "ui-warning" }
> = {
  queued: { label: "排队中", tone: "warning", icon: "ui-clock" },
  uploading: { label: "上传中", tone: "info", icon: "ui-upload" },
  failed: { label: "失败 · 会重试", tone: "warning", icon: "ui-refresh" },
  rejected: { label: "无法上传", tone: "danger", icon: "ui-warning" },
};

function QueueRow({ item }: { item: QueuedUpload }) {
  const s = STATE_LABEL[item.state];
  return (
    <li className="xz-queue__row">
      <Tag tone={s.tone}>
        <Icon id={s.icon} size={16} /> {s.label}
      </Tag>
      <span className="xz-queue__title">{item.title}</span>
      <span className="gz-caption">{formatDuration(item.duration_ms)}</span>
      {item.state === "rejected" && (
        <>
          <span className="gz-caption xz-queue__why">{item.message}</span>
          <Button variant="ghost" size="sm" onClick={() => void removeFromQueue(item.id)}>
            删掉
          </Button>
        </>
      )}
    </li>
  );
}

/**
 * docs/ROADMAP.md B2 — 「待上传 n 条」.
 *
 * Shown on 今日任务 and 我的录音 whenever the queue is not empty. Collapsed to a
 * single chip by default; the student can open it to see each take, and
 * 「现在重试」 ignores the backoff. Nothing here blocks recording — that is the
 * whole point of the queue.
 */
export function PendingUploads({ compact = false }: { compact?: boolean }) {
  const queue = useUploadQueue();
  const online = useOnline();
  const confirm = useConfirmSafe();
  const [open, setOpen] = useState(false);
  const n = pendingCount(queue);
  const rejected = queue.items.filter((i) => i.state === "rejected");

  if (queue.items.length === 0) return null;

  return (
    <Card padding="md" className="xz-queue">
      <div className="xz-queue__head">
        <Tag tone="warning">
          <Icon id="ui-upload" size={16} /> 待上传 {n} 条
        </Tag>
        {!compact && (
          <Button variant="ghost" size="sm" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            <Icon id={open ? "ui-collapse" : "ui-expand"} size={20} />
            {open ? "收起" : "看看是哪几条"}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          loading={queue.flushing}
          disabled={n === 0}
          onClick={() => void retryNow()}
        >
          <Icon id="ui-refresh" size={20} /> 现在重试
        </Button>
      </div>

      <p className="gz-caption xz-queue__hint">
        {online
          ? "这些录音已经存在手机里，正在自动补传，不用一直等着。"
          : "现在没有网络。录音已经存在手机里，联网后会自动补传。"}
      </p>

      {queue.quotaExceeded && (
        <p className="gz-notice gz-notice--error" role="alert">
          <Icon id="ui-warning" size={20} />
          手机存的东西太多，这条暂时存不下了。先别关掉这个页面，等联网后点「现在重试」把已有的传完。
        </p>
      )}

      {rejected.length > 0 && (
        <p className="gz-notice gz-notice--error" role="alert">
          <Icon id="ui-warning" size={20} />有 {rejected.length}{" "}
          条服务器没有接受，删掉后重录一次就好。
        </p>
      )}

      {(open || compact) && (
        <ul className="xz-queue__list">
          {queue.items.map((i) => (
            <QueueRow key={i.id} item={i} />
          ))}
        </ul>
      )}

      {open && (
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            const ok = await confirm({
              title: `还有 ${n} 条没传上去`,
              message: "清空之后这些录音就找不回来了，需要重新录。确定要清空吗？",
              confirmLabel: "仍然清空",
              cancelLabel: "再等等",
              danger: true,
            });
            if (ok) await clearQueue();
          }}
        >
          <Icon id="ui-trash" size={20} /> 清空待上传
        </Button>
      )}
    </Card>
  );
}
